import React, { useState, useCallback, useMemo } from 'react';
import Link from 'next/link';
import GraphQLErrorText from '../GraphQLErrorText';
import Loading from '../Loading';
import {
  WHITE_LABEL_ACCENT_COLOR,
  WHITE_LABEL_BRAND,
  WHITE_LABEL_LOGO,
} from '../../lib/white-label';
import Image from 'next/image';
import { BotListCard } from './BotListCard';
import { PartnerCheck } from './PartnerCheck';
import { Search, TrendingUp } from 'lucide-react';
import { useQuery } from '@apollo/client';
import { TRENDING_BOTS } from '@/lib/graphql/queries';

const BotsList = (props: {
  searchQuery: string;
  data: { bots: any[] };
  loading: boolean;
  error?: any;
  username?: string;
}) => {
  const [activeTab, setActiveTab] = useState('all');

  const {
    data: { trendingBots },
  } = useQuery(TRENDING_BOTS, {
    variables: {
      limit: 3,
    },
  });

  const getBotLink = useCallback(
    (b: any) => {
      if (props.username) {
        return b.status === 'ACTIVE'
          ? `/bots/seller/${props.username}/${b.id}`
          : '#';
      } else {
        return b.status === 'ACTIVE' ? `/bots/${b.id}` : '#';
      }
    },
    [props.username],
  );

  const sortedBots = useMemo(() => {
    if (props.loading) return [];
    return props.data.bots
      .filter((b) => b.status === 'ACTIVE' || b.status === 'COMING_SOON')
      .sort((a, b) => {
        if (a.listingMode === 'DIRECT' && b.listingMode !== 'DIRECT') return -1;
        if (a.listingMode !== 'DIRECT' && b.listingMode === 'DIRECT') return 1;
        return 0;
      });
  }, [props.data.bots]);

  if (props.loading) return <Loading />;
  if (props.error) return <GraphQLErrorText error={props.error} />;

  const partneredBots = sortedBots.filter((b) => b.listingMode === 'DIRECT');
  const sortedTrendingBots = sortedBots.filter((b) =>
    trendingBots.find((t) => t.botId === b.id),
  );

  const botsToRender =
    activeTab === 'trending'
      ? sortedTrendingBots
      : activeTab === 'partnered'
      ? partneredBots
      : sortedBots;

  return (
    <div className="mt-2 sm:mt-4 max-w-screen w-full">
      {sortedBots.length === 0 && !props.searchQuery ? (
        <div className="min-h-[41vh] flex justify-center w-full flex-col items-center gap-y-4">
          <Image
            src={WHITE_LABEL_LOGO}
            alt={`${WHITE_LABEL_BRAND} Logo`}
            width={240}
            height={120}
          />
          {!props.username && (
            <p>No bots available for rent currently, check back shortly.</p>
          )}
          {props.username && (
            <p>
              No bots available to rent from {props.username}.{' '}
              <Link
                className="underlink"
                style={{ color: WHITE_LABEL_ACCENT_COLOR }}
                href={'/bots'}
              >
                View all sellers
              </Link>
            </p>
          )}
        </div>
      ) : (
        <>
          <div className="flex flex-col space-y-4 mb-4">
            <div className="flex border-b border-gray-200">
              <button
                className={`px-4 py-2 font-medium text-sm ${
                  activeTab === 'all'
                    ? 'text-primary border-b-2 border-primary'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => setActiveTab('all')}
              >
                All Bots
              </button>
              <button
                className={`px-4 py-2 font-medium text-sm ${
                  activeTab === 'trending'
                    ? 'text-primary border-b-2 border-primary'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => setActiveTab('trending')}
              >
                <div className="flex items-center gap-2">
                  Trending{' '}
                  <TrendingUp width={18} color={WHITE_LABEL_ACCENT_COLOR} />
                </div>
              </button>
              <button
                className={`px-4 py-2 font-medium text-sm ${
                  activeTab === 'partnered'
                    ? 'text-primary border-b-2 border-primary'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                onClick={() => setActiveTab('partnered')}
              >
                <div className="flex items-center gap-2">
                  Partnered <PartnerCheck size={18} />
                </div>
              </button>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-2">
            {botsToRender.length === 0 && (
              <div className="w-full flex flex-col items-center justify-center py-12 text-gray-500">
                {activeTab === 'trending' ? (
                  <>
                    <TrendingUp width={24} />
                    <p>No trending bots matching criteria</p>
                  </>
                ) : activeTab === 'partnered' ? (
                  <>
                    <PartnerCheck size={24} />
                    <p>No partnered bots matching criteria</p>
                  </>
                ) : (
                  <>
                    <Search width={24} />
                    <p>No bots matching criteria</p>
                  </>
                )}
              </div>
            )}
            {botsToRender.map((b) => (
              <div key={b.id} className="w-full">
                <Link passHref href={getBotLink(b)}>
                  <BotListCard bot={b} />
                </Link>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default BotsList;
